import { UserPlus, ShieldCheck, Crown, Lightbulb } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Members & Roles";

export const tiles: ArticleTile[] = [
  { slug: "deputies-custom-roles", title: "Assigning Deputies and Custom Roles", category: CATEGORY, description: "Share responsibilities and create your own roles", icon: UserPlus },
  { slug: "dbs-checks", title: "DBS Check Recording & Tracking", category: CATEGORY, description: "Record DBS clearances and expiry dates", icon: ShieldCheck },
  { slug: "managing-pta-admins", title: "Managing PTA Admins (Max 3)", category: CATEGORY, description: "Promote members to admin and rotate access", icon: Crown },
];

export const articles: Record<string, ArticleContent> = {
  "deputies-custom-roles": {
    title: "Assigning Deputies and Custom Roles",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Share workload by assigning deputies, or create a "Custom" role for committee positions that don't fit the standard list.</Hero>
        <Section icon={UserPlus} title="Add a deputy">
          <Steps items={[
            <>Open the member you want to deputise in <strong>Members</strong>.</>,
            <>Toggle on the deputy switch next to the relevant role (e.g. Deputy Treasurer).</>,
            <>The deputy gets the same dashboard access as the primary role-holder.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Create a custom role">
          <Bullets items={[
            "Toggle on the Custom role on a member.",
            "Enter a friendly label (e.g. 'Class Rep Coordinator').",
            "The label shows on their badge across the platform.",
          ]} />
        </Section>
        <Tip>Roles tied to a paid add-on only appear if your subscription includes that add-on.</Tip>
      </div>
    ),
  },
  "dbs-checks": {
    title: "DBS Check Recording & Tracking",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Record DBS clearances against any member so you always know who is cleared to work directly with children.</Hero>
        <Section icon={ShieldCheck} title="Record a DBS check">
          <Steps items={[
            <>Open the member in <strong>Members</strong> and click <strong>Edit</strong>.</>,
            <>Tick <strong>DBS Cleared</strong>.</>,
            <>Enter the DBS certificate number and expiry date.</>,
            <>Save — a green DBS badge shows on their profile.</>,
          ]} />
        </Section>
        <Tip>The system flags expiring DBS checks 30 days before they lapse so you have time to renew.</Tip>
      </div>
    ),
  },
  "managing-pta-admins": {
    title: "Managing PTA Admins (Max 3)",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Each PTA can have up to 3 admins. Admins can manage subscription, payment provider, members and all settings.</Hero>
        <Section icon={Crown} title="Promote a member to admin">
          <Steps items={[
            <>Open the member in <strong>Members</strong> and click <strong>Edit</strong>.</>,
            <>Scroll to the bottom and toggle <strong>PTA Admin</strong>.</>,
            <>Save. They now have full administrative access.</>,
          ]} />
        </Section>
        <Tip>If you've already hit the 3-admin limit, demote an existing admin first by toggling their PTA Admin off.</Tip>
      </div>
    ),
  },
};
